import { useEffect, useMemo, useRef, useState } from "react";
import { LivechatSubjectType, LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Subject } from "@scrile/api-provider/dist/api/SubjectProvider";
import { parseISO } from "@scrile/tools/dist/lib/TimeHelpers";
import useAuthUser from "hooks/useAuthUser";
import useConsumeController from "hooks/useConsumeController";
import useLiveChat from "hooks/useLiveChat";
import useLiveChatName from "hooks/useLiveChatName";
import useProduceController from "hooks/useProduceController";
import useStreamHelper from "hooks/useStreamHelper";
import { ProduceUser } from "types";

export default function useProduceConsumeController(livechatSubject: Subject<LivechatSubjectType>, ownerId: string) {
  const { user } = useAuthUser();
  const { liveChatName } = useLiveChatName(user?.screenName ?? "");
  const { loading, token, joinData, endChat, clientServerTimeDiff, toggleMuteUser: muteUser } = useLiveChat({
    livechatSubject,
    liveChatName,
  });

  const {
    stream: produceStream,
    screenStream: produceScreenStream,
    mediaConstraints: produceMediaConstraints,
    closeStream: closeProduceStream,
    onChangeConstraints,
    audioInputs,
    videoInputs,
    processing,
    loading: loadingProduceData,
    producerSpeaking,
    permissions,
  } = useProduceController({ token, joinData, autoProduceStream: true, audioAnalyzer: true });

  const { closeStream: closeConsumeStream, users } = useConsumeController({
    joinData,
    token,
  });

  const { checkStreamForChanges } = useStreamHelper();

  const streamStartedDate = useMemo(() => {
    if (!joinData) {
      return "";
    }
    return new Date(parseISO(joinData.me.joinTime).getTime() + (clientServerTimeDiff ?? 0)).toISOString();
  }, [clientServerTimeDiff, joinData]);

  const localProduceStream = useRef<MediaStream>(produceStream);
  const localProduceScreenStream = useRef<MediaStream>(produceScreenStream);
  const userList = useMemo(() => joinData?.users.filter((user) => user.broadcasting) ?? [], [joinData]);
  const usersToView = useMemo(() => Object.values(users), [users]);
  const [me, setMe] = useState<ProduceUser | null>(null);

  useEffect(() => {
    if (!produceStream || !localProduceStream.current) return;
    checkStreamForChanges(produceStream, localProduceStream.current);
  }, [produceStream, checkStreamForChanges]);

  useEffect(() => {
    if (!produceScreenStream || !localProduceScreenStream.current) return;
    checkStreamForChanges(produceScreenStream, localProduceScreenStream.current);
  }, [produceScreenStream, checkStreamForChanges]);

  useEffect(() => {
    setMe(
      joinData
        ? {
            user: joinData.me,
            stream: localProduceStream.current,
            screenStream: localProduceScreenStream.current,
            constrains: produceMediaConstraints,
            isSpeaking: producerSpeaking,
          }
        : null
    );
  }, [joinData, produceMediaConstraints, localProduceStream, localProduceScreenStream, producerSpeaking]);

  useEffect(() => {
    if (me?.user?.muted) {
      onChangeConstraints({ audioEnabled: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.user?.muted]);

  useEffect(() => {
    window.addEventListener("beforeunload", endChat);
    return () => {
      window.removeEventListener("beforeunload", endChat);
    };
  }, [endChat]);

  const isConferenceOwner = useMemo(() => me?.user.userId === ownerId, [me, ownerId]);

  const toggleMuteUser = async (user: LiveChatUser) => {
    if (!isConferenceOwner) return;
    await muteUser(user);
  };

  const onCloseStream = async () => {
    closeProduceStream().then();
    closeConsumeStream();
    await Promise.all([endChat()]);
  };

  return {
    threadId: joinData?.threadId,
    userList,
    produceMediaConstraints,
    loading: loading || loadingProduceData || processing,
    token,
    audioInputs,
    videoInputs,
    users: usersToView,
    me,
    isConferenceOwner,
    streamStartedDate,
    onChangeConstraints,
    onCloseStream,
    toggleMuteUser,
    permissions,
  };
}
